import Button from '@components/buttonTsx';
import {OverlayedIcon} from '@components/icon';
import type ChatInput from '@components/chat/input';
import {ChatType} from '@components/chat/chatType';
import {toast, toastNew} from '@components/toast';
import {
  MessarioAiFetchFailure,
  requestMessarioAiDraft
} from '@lib/messarioAi/api';
import {
  buildMessarioAiContextFromMessages,
  type MessarioAiHistoryMediaLike
} from '@lib/messarioAi/context';
import {generateMessarioAiDraftLocal} from '@lib/messarioAi/generateFlow';
import {retryUnresolvedMessarioAiCompletionsForPeer} from '@lib/messarioAi/hooks';
import {resolveMessarioAiGenerateTransport} from '@lib/messarioAi/transport';
import {
  getMessarioAiPending,
  setMessarioAiPending
} from '@lib/messarioAi/pendingDraft';
import {getMessarioAiSession, isMessarioAiEnabledForUser} from '@lib/messarioAi/storage';
import {
  notifyMessarioAiTrainingDraftChanged,
  subscribeMessarioAiTrainingDraftChanged
} from '@lib/messarioAi/trainingDraftUi';
import {isMessarioUserDialogPeer} from '@lib/messarioTemplates/eligibility';
import {getMessarioTemplatesAccess} from '@lib/messarioTemplates/storage';
import classNames from '@helpers/string/classNames';
import {NULL_PEER_ID} from '@appManagers/constants';
import rootScope from '@lib/rootScope';
import track from '@helpers/solid/track';
import {resolveFirst} from '@solid-primitives/refs';
import {Accessor, createEffect, createMemo, createSignal, onCleanup} from 'solid-js';

type UseMessarioAiButtonArgs = {
  instance: ChatInput,
  /** Must read a Solid store flag (e.g. inputMessageContainerInited) so the effect re-runs. */
  appendTo: Accessor<HTMLDivElement | undefined>,
  forceHidden?: Accessor<boolean>
};

function currentExternalUserId(): string | undefined {
  if(!rootScope.myId || rootScope.myId === NULL_PEER_ID) {
    return undefined;
  }

  return String(rootScope.myId);
}

function panelOrigin(): string {
  return import.meta.env.VITE_MESSARIO_PANEL_ORIGIN || '';
}

async function collectContextMessages(instance: ChatInput, historyLimit: number) {
  const historyStorage = instance.chat.getHistoryStorage();
  const first = historyStorage?.history?.first;
  const midsNewestFirst = (Array.isArray(first) ? [...first] : []) as number[];
  const messages = midsNewestFirst.map((mid) => {
    const message = instance.chat.getMessage(mid) as {
      _?: string,
      message?: string,
      pFlags?: {out?: boolean},
      media?: MessarioAiHistoryMediaLike | null,
      grouped_id?: string | number
    } | undefined;

    return {
      _: message?._,
      message: message?.message,
      pFlags: message?.pFlags,
      media: message?.media ?? undefined,
      grouped_id: message?.grouped_id
    };
  });

  return buildMessarioAiContextFromMessages(messages, historyLimit);
}

function lastIncomingCandidateText(
  messages: Array<{role: 'user' | 'assistant', content: string}>
): string {
  for(let i = messages.length - 1; i >= 0; i--) {
    if(messages[i].role === 'user' && messages[i].content.trim()) {
      return messages[i].content.trim();
    }
  }
  return '';
}

function messarioAiGenerateToast(err: unknown): string {
  if(err instanceof MessarioAiFetchFailure) {
    if(err.errorCode === 'register_network') {
      return 'Черновик получен от OpenAI, но Panel не сохранил его. Повторите Generate.';
    }
    if(err.errorCode === 'outdated_ai_bundle' || err.status === 409) {
      return 'Настройки AI обновились. Повторите Generate.';
    }
    if(err.source === 'openai') {
      if(err.kind === 'timeout') {
        return 'Messario AI: превышено время ожидания OpenAI (90 с). Проверьте сеть или VPN.';
      }
      if(err.kind === 'network') {
        return 'Не удалось связаться с OpenAI. Проверьте сеть или VPN.';
      }
      if(err.kind === 'rejected') {
        return `OpenAI недоступен${typeof err.status === 'number' ? ` (${err.status})` : ''}.`;
      }
      if(err.kind === 'invalid-response') {
        return 'Модель вернула некорректный structured output. Повторите Generate.';
      }
    }
    if(err.message.trim()) {
      return err.message;
    }
  }

  return 'Не удалось сгенерировать черновик Messario AI';
}

/**
 * Optional one-shot instruction before generate (same idea as Panel Messenger).
 * Cancel → abort. Empty input → generate without one_shot.
 * confirmationPopup / InputField are lazy-imported to avoid PopupPeer TDZ cycles.
 */
async function askOneShotInstruction(): Promise<string | undefined> {
  const [
    {default: confirmationPopup},
    {default: InputField},
    {styleMessarioSheetPopup},
    {default: PopupElement}
  ] = await Promise.all([
    import('@components/confirmationPopup'),
    import('@components/inputField'),
    import('@lib/messarioSheetPopup'),
    import('@components/popups/indexTsx')
  ]);

  const inputField = new InputField({
    maxLength: 2000,
    required: false,
    plainText: true,
    withLinebreaks: true,
    labelText: 'Инструкция (необязательно)'
  });

  try {
    const popupOptions: Parameters<typeof confirmationPopup>[0] = {
      title: 'Messario AI',
      descriptionRaw: 'Можно добавить краткую подсказку только для этой генерации.',
      inputField,
      button: {text: document.createTextNode('Сгенерировать')},
      className: 'messario-ai-oneshot-popup',
      closable: true,
      onPopup: () => {
        // Solid popup shell mounts synchronously: newest POPUPS entry is this popup.
        styleMessarioSheetPopup({
          element: PopupElement.POPUPS[PopupElement.POPUPS.length - 1]?.element,
          buttons: popupOptions.buttons
        });
      }
    };
    await confirmationPopup(popupOptions);
  } catch{
    return undefined;
  }

  return String(inputField.value || '').trim();
}

export function useMessarioAiButton({
  instance,
  appendTo,
  forceHidden = () => false
}: UseMessarioAiButtonArgs) {
  const [busy, setBusy] = createSignal(false);
  // peer_changed fires after finishPeerChange, when chat.isBot is already updated.
  const [dialogEligible, setDialogEligible] = createSignal(
    isMessarioUserDialogPeer(instance.chat.peerId, instance.chat.isBot)
  );

  createEffect(() => {
    const refresh = () => {
      setDialogEligible(isMessarioUserDialogPeer(instance.chat.peerId, instance.chat.isBot));
    };
    refresh();
    instance.chat.appImManager.addEventListener('peer_changed', refresh);
    onCleanup(() => {
      instance.chat.appImManager.removeEventListener('peer_changed', refresh);
    });
  });

  const canShow = createMemo(() => {
    if(forceHidden()) return false;
    if(instance.chat.isPreview || instance.chat.type === ChatType.Stories) return false;
    if(!dialogEligible()) return false;
    const userId = currentExternalUserId();
    if(!userId) return false;
    return isMessarioAiEnabledForUser(userId);
  });

  createEffect(() => {
    track(canShow); // appendTo may become ready after first paint
    const mount = appendTo();
    if(!canShow() || !mount) return;

    let generating = false;

    const icon = OverlayedIcon([
      'ai_letters',
      {icon: 'ai_star1', className: 'chat-input-ai-editor-button__star-1'},
      {icon: 'ai_star2', className: 'chat-input-ai-editor-button__star-2'}
    ], 'chat-input-messario-ai-button__icon');

    const button = Button({
      class: classNames('chat-input-messario-ai-button', 'btn-icon'),
      children: icon,
      'aria-label': 'Messario AI',
      onClick: async() => {
        if(generating || busy()) return;

        const userId = currentExternalUserId();
        if(!userId) return;

        const session = getMessarioAiSession(userId);
        const access = getMessarioTemplatesAccess(userId);
        if(!session?.ai_bundle.enabled || !access?.token) {
          toastNew({langPackKey: 'Error.AnError'});
          return;
        }

        const peerId = instance.chat.peerId;
        if(!peerId) return;

        let oneShot: string | undefined;
        if(session.ai_bundle.features.one_shot_instruction) {
          const asked = await askOneShotInstruction();
          if(asked === undefined) {
            return; // cancelled
          }
          oneShot = asked || undefined;
        }

        const historyLimit = session.ai_bundle.history_limit || 40;

        generating = true;
        setBusy(true);
        try {
          // Preflight: finish Panel /complete for prior Telegram-confirmed sends on this peer.
          try {
            await retryUnresolvedMessarioAiCompletionsForPeer(session.account_id, peerId);
          } catch(err) {
            const message = err instanceof MessarioAiFetchFailure ?
              err.message :
              (err instanceof Error && err.message.trim() ?
                err.message :
                'Не удалось подтвердить предыдущую отправку Messario AI. Повторите Generate позже.');
            toast(message);
            console.warn('Messario AI unresolved complete preflight failed', err);
            return;
          }

          const messages = await collectContextMessages(instance, historyLimit);
          const transport = resolveMessarioAiGenerateTransport(session.ai_bundle);
          const payload: {
            peer_id: string,
            messages: Awaited<ReturnType<typeof collectContextMessages>>,
            one_shot_instruction?: string
          } = {
            peer_id: String(peerId),
            messages
          };
          if(oneShot) {
            payload.one_shot_instruction = oneShot;
          }

          const result = transport === 'local' ?
            await generateMessarioAiDraftLocal({
              token: access.token,
              panelOrigin: panelOrigin(),
              externalUserId: userId,
              peerId: String(peerId),
              messages,
              oneShot,
              metadata: session.ai_bundle
            }) :
            await requestMessarioAiDraft(
              access.token,
              panelOrigin(),
              payload
            );

          const reply = String(result.reply || '').trim();
          if(!reply) {
            toastNew({langPackKey: 'Error.AnError'});
            return;
          }

          instance.messageInputField?.setDraftValue(reply);

          // Keep pending for training-event complete/dismiss and/or Training Analyzer.
          if(result.training_event_id != null || result.draft_generate_id != null) {
            setMessarioAiPending(session.account_id, peerId, {
              training_event_id: result.training_event_id ?? null,
              draft_generate_id: result.draft_generate_id ?? null,
              reply_text: reply,
              account_id: session.account_id,
              peer_id: String(peerId)
            });
          }
        } catch(err) {
          const message = messarioAiGenerateToast(err);
          toast(message);
          console.warn('Messario AI draft failed', err);
        } finally {
          generating = false;
          setBusy(false);
          // Notify after busy=false: visibility used to hide while busy, so an
          // earlier notify left «Разобрать ошибку» stuck with class hide.
          notifyMessarioAiTrainingDraftChanged();
        }
      }
    });

    const child = resolveFirst(() => button);
    const element = child();
    if(!element) return;

    // Place before smile/emoticons in the compose icon row (not absolute over it).
    if(instance.btnToggleEmoticons && instance.btnToggleEmoticons.parentElement === mount) {
      mount.insertBefore(element, instance.btnToggleEmoticons);
    } else {
      mount.append(element);
    }

    onCleanup(() => {
      element.remove();
    });
  });

  // Secondary control «Разобрать ошибку» — mount on chatInput ABOVE
  // templates strip (then composer), not under «Шаблон…».
  createEffect(() => {
    track(canShow);
    // Same readiness gate as the AI icon (newMessageWrapper exists ⇒ chatInput ready).
    if(!canShow() || !appendTo()) return;

    const mount = instance.chatInput;
    const inputContainer = instance.inputContainer;
    if(!mount || !inputContainer) return;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chat-input-messario-ai-training hide';
    btn.setAttribute('aria-label', 'Разобрать ошибку Messario AI');

    const icon = OverlayedIcon([
      'ai_letters',
      {icon: 'ai_star1', className: 'chat-input-ai-editor-button__star-1'},
      {icon: 'ai_star2', className: 'chat-input-ai-editor-button__star-2'}
    ], 'chat-input-messario-ai-training__icon');
    const label = document.createElement('span');
    label.className = 'chat-input-messario-ai-training__label';
    label.textContent = 'Разобрать ошибку';
    btn.append(icon, label);

    const refreshVisibility = () => {
      const userId = currentExternalUserId();
      const session = userId ? getMessarioAiSession(userId) : undefined;
      const peerId = instance.chat.peerId;
      const pending = session && peerId ?
        getMessarioAiPending(session.account_id, peerId) :
        undefined;
      const hasDraftId = Number(pending?.draft_generate_id) > 0;
      // Hide only when there is no pending draft. Busy only disables the control —
      // tying hide to busy left the button stuck after generate (notify ran while busy).
      btn.classList.toggle('hide', !hasDraftId);
      btn.disabled = busy() || !hasDraftId;
    };

    const unsubDraft = subscribeMessarioAiTrainingDraftChanged(refreshVisibility);
    const onPeerChanged = () => refreshVisibility();
    instance.chat.appImManager.addEventListener('peer_changed', onPeerChanged);

    btn.addEventListener('click', async() => {
      if(busy()) return;

      const userId = currentExternalUserId();
      if(!userId) return;

      const session = getMessarioAiSession(userId);
      const access = getMessarioTemplatesAccess(userId);
      const peerId = instance.chat.peerId;
      if(!session || !access?.token || !peerId) {
        toastNew({langPackKey: 'Error.AnError'});
        return;
      }

      const pending = getMessarioAiPending(session.account_id, peerId);
      const draftGenerateId = Number(pending?.draft_generate_id);
      if(!(draftGenerateId > 0)) {
        toast('Нет draft_generate_id для разбора. Сгенерируйте черновик заново.');
        refreshVisibility();
        return;
      }

      const historyLimit = session.ai_bundle.enabled ?
        (session.ai_bundle.history_limit || 40) :
        40;
      const messages = await collectContextMessages(instance, historyLimit);
      const candidateMessage = lastIncomingCandidateText(messages);

      // Lazy-import modal (confirmationPopup / PopupPeer) — keep button.ts free of static UI cycles.
      const {openMessarioAiTrainingAnalyzeFlow} = await import('@lib/messarioAi/trainingModal');
      await openMessarioAiTrainingAnalyzeFlow({
        token: access.token,
        panelOrigin: panelOrigin(),
        draftGenerateId,
        aiReplyText: String(pending?.reply_text || ''),
        candidateMessage: candidateMessage || undefined,
        history: messages.slice(-6)
      });
    });

    // Order: this button → templates strip (if any) → composer row.
    const templates = mount.querySelector('.messario-template-search');
    if(templates && templates.parentElement === mount) {
      mount.insertBefore(btn, templates);
    } else if(inputContainer.parentElement === mount) {
      mount.insertBefore(btn, inputContainer);
    } else {
      mount.append(btn);
    }
    refreshVisibility();

    onCleanup(() => {
      unsubDraft();
      instance.chat.appImManager.removeEventListener('peer_changed', onPeerChanged);
      btn.remove();
    });
  });
}
